import React from 'react';

import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const CaseFilters = ({ 
  filters, 
  onFilterChange, 
  onClearFilters, 
  language = 'en',
  direction = 'ltr' 
}) => {
  const labels = {
    search: {
      en: 'Search cases...',
      fr: 'Rechercher des dossiers...',
      ar: 'البحث عن القضايا...'
    },
    practiceArea: {
      en: 'Practice Area',
      fr: 'Domaine de pratique',
      ar: 'مجال الممارسة'
    },
    status: {
      en: 'Case Status',
      fr: 'Statut du dossier',
      ar: 'حالة القضية'
    },
    lawyer: {
      en: 'Assigned Lawyer',
      fr: 'Avocat assigné',
      ar: 'المحامي المعين'
    },
    dateFrom: {
      en: 'Date From',
      fr: 'Date de début',
      ar: 'من تاريخ'
    },
    dateTo: {
      en: 'Date To',
      fr: 'Date de fin',
      ar: 'إلى تاريخ'
    },
    clearFilters: {
      en: 'Clear Filters',
      fr: 'Effacer les filtres',
      ar: 'مسح الفلاتر'
    },
    allAreas: {
      en: 'All Practice Areas',
      fr: 'Tous les domaines',
      ar: 'جميع المجالات'
    },
    allStatuses: {
      en: 'All Statuses',
      fr: 'Tous les statuts',
      ar: 'جميع الحالات'
    },
    allLawyers: {
      en: 'All Lawyers',
      fr: 'Tous les avocats',
      ar: 'جميع المحامين'
    }
  };

  const practiceAreaOptions = [
    { value: '', label: labels?.allAreas?.[language] },
    { 
      value: 'civil', 
      label: language === 'ar' ? 'القانون المدني' : language === 'fr' ? 'Droit civil' : 'Civil Law' 
    },
    { 
      value: 'commercial', 
      label: language === 'ar' ? 'القانون التجاري' : language === 'fr' ? 'Droit commercial' : 'Commercial Law' 
    },
    { 
      value: 'family', 
      label: language === 'ar' ? 'قانون الأسرة' : language === 'fr' ? 'Droit de la famille' : 'Family Law' 
    },
    { 
      value: 'criminal', 
      label: language === 'ar' ? 'القانون الجنائي' : language === 'fr' ? 'Droit pénal' : 'Criminal Law' 
    },
    { 
      value: 'labor', 
      label: language === 'ar' ? 'قانون العمل' : language === 'fr' ? 'Droit du travail' : 'Labor Law' 
    },
    { 
      value: 'real-estate', 
      label: language === 'ar' ? 'القانون العقاري' : language === 'fr' ? 'Droit immobilier' : 'Real Estate Law' 
    }
  ];

  const statusOptions = [
    { value: '', label: labels?.allStatuses?.[language] },
    { 
      value: 'intake', 
      label: language === 'ar' ? 'استقبال' : language === 'fr' ? 'Réception' : 'Intake' 
    },
    { 
      value: 'investigation', 
      label: language === 'ar' ? 'تحقيق' : language === 'fr' ? 'Enquête' : 'Investigation' 
    },
    { 
      value: 'litigation', 
      label: language === 'ar' ? 'تقاضي' : language === 'fr' ? 'Contentieux' : 'Litigation' 
    },
    { 
      value: 'negotiation', 
      label: language === 'ar' ? 'تفاوض' : language === 'fr' ? 'Négociation' : 'Negotiation' 
    },
    { 
      value: 'appeal', 
      label: language === 'ar' ? 'استئناف' : language === 'fr' ? 'Appel' : 'Appeal' 
    },
    { 
      value: 'closed', 
      label: language === 'ar' ? 'مغلق' : language === 'fr' ? 'Clôturé' : 'Closed' 
    }
  ];

  const lawyerOptions = [
    { value: '', label: labels?.allLawyers?.[language] },
    { value: 'ahmed-benali', label: 'Ahmed Benali' },
    { value: 'fatima-zahra', label: 'Fatima Zahra El Amrani' },
    { value: 'youssef-mansouri', label: 'Youssef Mansouri' },
    { value: 'sara-idrissi', label: 'Sara Idrissi' },
    { value: 'karim-alaoui', label: 'Karim Alaoui' }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6 mb-4 md:mb-6" style={{ direction }}>
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
        <h3 className="text-base md:text-lg font-semibold text-foreground">
          {language === 'ar' ? 'تصفية القضايا' : language === 'fr' ? 'Filtrer les dossiers' : 'Filter Cases'}
        </h3>
        <Button
          variant="ghost"
          size="sm"
          iconName="X"
          iconPosition="left"
          onClick={onClearFilters}
        >
          {labels?.clearFilters?.[language]}
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-3">
          <Input
            type="search"
            placeholder={labels?.search?.[language]}
            value={filters?.search}
            onChange={(e) => onFilterChange('search', e?.target?.value)}
            className="w-full"
          />
        </div>

        <Select
          label={labels?.practiceArea?.[language]}
          options={practiceAreaOptions}
          value={filters?.practiceArea}
          onChange={(value) => onFilterChange('practiceArea', value)}
        />

        <Select
          label={labels?.status?.[language]}
          options={statusOptions}
          value={filters?.status}
          onChange={(value) => onFilterChange('status', value)}
        />

        <Select
          label={labels?.lawyer?.[language]}
          options={lawyerOptions}
          value={filters?.assignedLawyer}
          onChange={(value) => onFilterChange('assignedLawyer', value)}
        />

        <Input
          type="date"
          label={labels?.dateFrom?.[language]}
          value={filters?.dateFrom}
          onChange={(e) => onFilterChange('dateFrom', e?.target?.value)}
        />

        <Input
          type="date"
          label={labels?.dateTo?.[language]}
          value={filters?.dateTo}
          onChange={(e) => onFilterChange('dateTo', e?.target?.value)}
        />
      </div>
    </div>
  );
};

export default CaseFilters;